import { useState, useEffect, useRef } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ShieldCheck, Search, MapPin, Calendar, Loader2, FileDown, Upload, BarChart3, ClipboardList, Clock, XCircle, AlertTriangle, Ban, ParkingCircle, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format, differenceInCalendarDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { usePermissions } from '../contexts/PermissionsContext';
import { useAuth } from '../contexts/AuthContext';
import { listsEqualById } from '../lib/listDiff';
import { AotStatusHistoryDialog } from '../components/aot/AotStatusHistoryDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUTS = {
    a_demander: { label: 'A demander', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    hors_delais: { label: 'Hors delais', color: 'bg-red-100 text-red-700 border-red-200' },
    en_cours: { label: 'En cours', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    accepte: { label: 'Accepte', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    refuse: { label: 'Refuse', color: 'bg-red-100 text-red-700 border-red-200' },
};

const TRANSITIONS = {
    a_demander: ['en_cours', 'hors_delais'],
    hors_delais: ['en_cours'],
    en_cours: ['accepte', 'refuse'],
    refuse: ['en_cours'],
    accepte: [],
};

function formatDate(d) { if (!d) return '-'; try { return format(new Date(d), 'dd/MM/yyyy', { locale: fr }); } catch { return d; } }

export default function AotPage() {
    const { user } = useAuth();
    const { can } = usePermissions();
    const readOnly = !can('aot', 'write');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState(null);
    const [uploading, setUploading] = useState(null);
    const fileInputRef = useRef(null);
    const pendingAcceptId = useRef(null);
    const [historyAot, setHistoryAot] = useState(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);

    const fetchData = (silent = false) => {
        if (!silent) setLoading(true);
        axios.get(`${API}/api/aot`, { withCredentials: true })
            .then(res => setItems(prev => listsEqualById(prev, res.data) ? prev : res.data))
            .catch(() => { if (!silent) toast.error('Erreur de chargement'); })
            .finally(() => { if (!silent) setLoading(false); });
    };
    useEffect(() => { fetchData(); }, []);

    useAutoRefresh(() => fetchData(true));

    const handleStatut = async (id, statut) => {
        if (statut === 'accepte') {
            const item = items.find(i => i.id === id);
            if (!item?.has_file) {
                pendingAcceptId.current = id;
                fileInputRef.current?.click();
                return;
            }
        }
        try {
            await axios.patch(`${API}/api/aot/${id}/statut?statut=${statut}`, {}, { withCredentials: true });
            // Optimistic update : reflète immédiatement le nouveau statut dans l'UI
            setItems(prev => prev.map(it => it.id === id ? { ...it, statut } : it));
            toast.success('Statut mis a jour');
            fetchData(true); // refresh silencieux en arrière-plan (champs dérivés)
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !pendingAcceptId.current) return;
        const id = pendingAcceptId.current;
        setUploading(id);
        try {
            const formData = new FormData();
            formData.append('file', file);
            await axios.post(`${API}/api/aot/${id}/upload-file`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success('Fichier AOT joint');
            await axios.patch(`${API}/api/aot/${id}/statut?statut=accepte`, {}, { withCredentials: true });
            // Optimistic update : statut + has_file passent immédiatement
            setItems(prev => prev.map(it => it.id === id ? { ...it, statut: 'accepte', has_file: true, aot_filename: file.name } : it));
            toast.success('Statut mis a jour : Accepte');
            fetchData(true);
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur upload'); }
        finally { setUploading(null); pendingAcceptId.current = null; e.target.value = ''; }
    };

    const handleDownloadFile = async (id, filename) => {
        try {
            const res = await axios.get(`${API}/api/aot/${id}/file`, { withCredentials: true, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || 'AOT.pdf';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch { toast.error('Erreur telechargement'); }
    };

    const isParis = (adresse) => {
        if (!adresse) return false;
        const a = adresse.toLowerCase();
        return a.includes('paris') || /\b75\d{3}\b/.test(a);
    };

    const isUrgent = (item) => {
        if (!item.date_demenagement) return false;
        const jours = differenceInCalendarDays(new Date(item.date_demenagement), new Date());
        if (jours < 0) return false;
        if (item.statut === 'a_demander') {
            const seuil = isParis(item.adresse) ? 12 : 25;
            return jours <= seuil;
        }
        if (item.statut === 'en_cours' && jours <= 5) return true;
        return false;
    };
    const urgentCount = items.filter(isUrgent).length;

    const filtered = items.filter(c => {
        if (activeFilter === 'a_demander' && c.statut !== 'a_demander') return false;
        if (activeFilter === 'en_cours' && c.statut !== 'en_cours') return false;
        if (activeFilter === 'hors_delais' && c.statut !== 'hors_delais') return false;
        if (activeFilter === 'refuse' && c.statut !== 'refuse') return false;
        if (activeFilter === 'urgent' && !isUrgent(c)) return false;
        if (!search) return true;
        const s = search.toLowerCase();
        return (c.client_nom || '').toLowerCase().includes(s) || (c.adresse || '').toLowerCase().includes(s);
    }).sort((a, b) => (isUrgent(b) ? 1 : 0) - (isUrgent(a) ? 1 : 0));

    const total = items.length;
    const aDemander = items.filter(i => i.statut === 'a_demander').length;
    const enCours = items.filter(i => i.statut === 'en_cours').length;
    const horsDelais = items.filter(i => i.statut === 'hors_delais').length;
    const refuse = items.filter(i => i.statut === 'refuse').length;

    const getOptions = (currentStatut) => {
        const allowed = TRANSITIONS[currentStatut] || [];
        return [currentStatut, ...allowed];
    };

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="aot-page">
            <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileUpload} data-testid="aot-file-input" />

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>AOT</h1>
                    <p className="mt-1 text-slate-500">Autorisations de stationnement</p>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6" data-testid="aot-stats">
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'all' ? null : 'all')} className={`text-left transition-all ${activeFilter === 'all' ? 'ring-2 ring-slate-400' : 'hover:shadow-md'}`} data-testid="aot-filter-total">
                    <Card className="bg-white rounded-xl border border-slate-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                                <BarChart3 size={20} className="text-slate-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-slate-900" data-testid="aot-stat-total">{total}</p>
                                <p className="text-xs text-slate-500">Total AOT</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'a_demander' ? null : 'a_demander')} className={`text-left transition-all ${activeFilter === 'a_demander' ? 'ring-2 ring-amber-400' : 'hover:shadow-md'}`} data-testid="aot-filter-a-demander">
                    <Card className="bg-white rounded-xl border border-amber-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                                <ClipboardList size={20} className="text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-amber-700" data-testid="aot-stat-a-demander">{aDemander}</p>
                                <p className="text-xs text-slate-500">A demander</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'en_cours' ? null : 'en_cours')} className={`text-left transition-all ${activeFilter === 'en_cours' ? 'ring-2 ring-blue-400' : 'hover:shadow-md'}`} data-testid="aot-filter-en-cours">
                    <Card className="bg-white rounded-xl border border-blue-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                                <Clock size={20} className="text-blue-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-blue-700" data-testid="aot-stat-en-cours">{enCours}</p>
                                <p className="text-xs text-slate-500">En cours</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'hors_delais' ? null : 'hors_delais')} className={`text-left transition-all ${activeFilter === 'hors_delais' ? 'ring-2 ring-orange-400' : 'hover:shadow-md'}`} data-testid="aot-filter-hors-delais">
                    <Card className="bg-white rounded-xl border border-orange-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                                <Ban size={20} className="text-orange-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-orange-700" data-testid="aot-stat-hors-delais">{horsDelais}</p>
                                <p className="text-xs text-slate-500">Hors délais</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'refuse' ? null : 'refuse')} className={`text-left transition-all ${activeFilter === 'refuse' ? 'ring-2 ring-rose-400' : 'hover:shadow-md'}`} data-testid="aot-filter-refuse">
                    <Card className="bg-white rounded-xl border border-rose-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center">
                                <XCircle size={20} className="text-rose-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-rose-700" data-testid="aot-stat-refuse">{refuse}</p>
                                <p className="text-xs text-slate-500">Refusé</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'urgent' ? null : 'urgent')} className={`text-left transition-all ${activeFilter === 'urgent' ? 'ring-2 ring-red-400' : 'hover:shadow-md'}`} data-testid="aot-filter-urgent">
                    <Card className={`bg-white rounded-xl border shadow-sm h-full ${urgentCount > 0 ? 'border-red-300 bg-red-50/30' : 'border-slate-200'}`}>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${urgentCount > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                                <AlertTriangle size={20} className={urgentCount > 0 ? 'text-red-600' : 'text-slate-400'} />
                            </div>
                            <div>
                                <p className={`text-2xl font-bold ${urgentCount > 0 ? 'text-red-600' : 'text-slate-400'}`} data-testid="aot-stat-urgent">{urgentCount}</p>
                                <p className="text-xs text-slate-500">Urgent</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
            </div>

            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4">
                    <div className="relative flex-1">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input placeholder="Rechercher (nom, adresse)..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="aot-search" />
                    </div>
                </CardContent>
            </Card>

            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <ShieldCheck size={20} className="text-[#2e7cd6]" />
                        Liste des AOT ({filtered.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" /></div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">Aucune AOT</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table className="min-w-[700px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Client</TableHead>
                                        <TableHead className="w-10"></TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Adresse</TableHead>
                                        <TableHead>Date demenagement</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map(item => {
                                        const allowed = TRANSITIONS[item.statut] || [];
                                        const isLocked = allowed.length === 0;
                                        return (
                                        <TableRow key={item.id} data-testid={`aot-row-${item.id}`} className={isUrgent(item) ? 'bg-red-50/50' : ''}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                                                        <ParkingCircle size={20} className="text-[#2e7cd6]" />
                                                    </div>
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <p className="font-medium text-slate-900 truncate">{item.client_nom}</p>
                                                        {isUrgent(item) && (
                                                            <Badge className="bg-red-500 text-white border-0 text-[10px] px-1.5 py-0 h-5 animate-pulse" data-testid={`aot-urgent-${item.id}`}>
                                                                URGENT
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="w-10">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setHistoryAot({ id: item.id, client_nom: item.client_nom });
                                                        setIsHistoryOpen(true);
                                                    }}
                                                    className="p-1 rounded-md hover:bg-blue-50 transition-colors"
                                                    title="Historique des statuts"
                                                    data-testid={`aot-star-${item.id}`}
                                                >
                                                    <Star size={18} className="text-[#2e7cd6] fill-[#2e7cd6]" />
                                                </button>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={item.type === 'depart' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}>
                                                    {item.type === 'depart' ? 'Depart' : 'Arrivee'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-start gap-1.5">
                                                    <MapPin size={12} className={item.type === 'depart' ? 'text-emerald-500 mt-0.5' : 'text-red-500 mt-0.5'} />
                                                    <span className="text-sm text-slate-600 line-clamp-1">{item.adresse || '-'}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5">
                                                    <Calendar size={12} className="text-slate-400" />
                                                    <span className={`text-sm ${item.date_demenagement ? 'text-slate-700' : 'text-slate-400 italic'}`}>{item.date_demenagement ? formatDate(item.date_demenagement) : 'Non programmé'}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {(isLocked || readOnly) ? (
                                                    <Badge variant="outline" className={`text-xs border ${(STATUTS[item.statut] || STATUTS.a_demander).color}`}>
                                                        {(STATUTS[item.statut] || STATUTS.a_demander).label}
                                                    </Badge>
                                                ) : (
                                                    <Select value={item.statut} onValueChange={(v) => handleStatut(item.id, v)}>
                                                        <SelectTrigger className="w-[150px] h-8" data-testid={`aot-statut-${item.id}`}>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {getOptions(item.statut).map(v => {
                                                                const info = STATUTS[v] || STATUTS.a_demander;
                                                                return (
                                                                    <SelectItem key={v} value={v}>
                                                                        <span className={`inline-block px-2 py-0.5 rounded text-xs ${info.color}`}>{info.label}</span>
                                                                    </SelectItem>
                                                                );
                                                            })}
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                                {uploading === item.id && <Loader2 size={14} className="inline ml-2 animate-spin text-[#2e7cd6]" />}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1">
                                                    {item.has_file && (
                                                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" onClick={() => handleDownloadFile(item.id, item.aot_filename)} data-testid={`aot-download-${item.id}`}>
                                                            <FileDown size={13} className="text-[#2e7cd6]" />
                                                            AOT
                                                        </Button>
                                                    )}
                                                    {user?.role === 'admin' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-7 w-7"
                                                            onClick={async () => {
                                                                if (!window.confirm(`Supprimer cet AOT (${item.client_nom}) ? Action irréversible.`)) return;
                                                                try {
                                                                    await axios.delete(`${API}/api/aot/${item.id}`, { withCredentials: true });
                                                                    toast.success('AOT supprimé');
                                                                    fetchData();
                                                                } catch (err) {
                                                                    toast.error('Erreur: ' + (err?.response?.data?.detail || err?.message));
                                                                }
                                                            }}
                                                            data-testid={`aot-delete-${item.id}`}
                                                            title="Supprimer (admin)"
                                                        >
                                                            <Trash2 size={14} className="text-red-500" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <AotStatusHistoryDialog
                open={isHistoryOpen}
                onOpenChange={setIsHistoryOpen}
                aot={historyAot}
            />
        </div>
    );
}
